import { useCallback, useEffect, useMemo, useRef } from 'react';
// import { interval } from 'd3-timer';
import {
  getDocumentRotationInfo,
  getScrollContainer,
  getScrollInfo,
  getZoomScaleInfo,
  makeMarkDisappear,
} from '../utils';
import { safeGetJSON } from './usePresentView';
import { useSelector } from 'react-redux';
import {
  selectCurrentCase,
  selectCurrentFolder,
  selectCurrentSelectedFileMetaData,
  selectIsGlobalPageLookup,
  selectMarkBasedScrolling,
  selectMatchedUrl,
  selectPresentQuery,
  selectQuery,
} from 'common/selectors';
import {
  HIGHLIGHTSDISAPPEAR,
  SCROLL_MARK_TYPE,
  PRESENT_COLOR,
  PAGENUMBER_MARK_TYPE,
} from '../constants';
import { MouseTool, MouseTools, Mark } from '@prizmdoc/viewer-core';
import { useStartPresenting } from 'features/viewing/redux/startPresenting';
import { useSendMessage } from 'features/viewing/redux/sendMessage';
import history from 'common/history';
import { makeCancelable } from 'utils/promises';
import { useLeaveAGroup } from 'features/viewing/redux/leaveAGroup';
import logger from 'utils/logger';
import { useDispatch } from 'react-redux';
import { urlChanged } from 'features/matcher/redux/routeChanged';

export const useSendMessages = ({
  passViewingSessionIdToReceivers,
  firstParentTriaBundleFileDetails,
  fileId,
  options,
  viewingFilePrivate,
  viewerControlRef,
  setCurrentMouseTool,
  selectedMouseTool,
  deleteMarkToBeCreated,
}: any) => {
  const { sendMessage: sendMessageApi } = useSendMessage();
  const { leaveAGroup } = useLeaveAGroup();
  const { startPresenting } = useStartPresenting();

  const present = useSelector(selectPresentQuery);
  const currentCase = useSelector(selectCurrentCase);
  const currentFolder = useSelector(selectCurrentFolder);
  const currentFileMetaData = useSelector(selectCurrentSelectedFileMetaData) as any;
  const isGlobalPageLookup = useSelector(selectIsGlobalPageLookup);
  const queryParam = useSelector(selectQuery);
  const currentUrl = useSelector(selectMatchedUrl);
  const markBasedScrolling = useSelector(selectMarkBasedScrolling);

  const showGlobalPaging = currentFolder && currentFolder.globalPaging;
  const isPresentModePage = currentUrl.includes(`${currentCase.id}/present-`);
  const isPresenting = !!present;

  const leaveGroupSafeRef = useRef<any>(null);
  const startPresentingSafeRef = useRef<any>(null);
  const addCenterHelperMarkSafeRef = useRef<any>(null);
  const presentationContextRef = useRef<any>({});

  const mergePresentationContext = useCallback(
    (payload: any) => {
      if (!payload) return;

      const nextContext = { ...presentationContextRef.current };
      const shallowKeys = [
        'fileType',
        'name',
        'showGlobalPaging',
        'viewingSessionId',
        'prizmApiUrl',
        'viewingFilePrivate',
      ];

      shallowKeys.forEach((key) => {
        if (typeof payload[key] !== 'undefined') {
          nextContext[key] = payload[key];
        }
      });

      if (payload.currentFileDetails) {
        nextContext.currentFileDetails = {
          ...nextContext.currentFileDetails,
          ...payload.currentFileDetails,
        };
      }

      if (payload.fileId || fileId) {
        nextContext.fileId = payload.fileId || fileId;
      }

      presentationContextRef.current = nextContext;
    },
    [fileId],
  );

  const resetPresentationContext = useCallback(() => {
    presentationContextRef.current = {};
  }, []);

  const dispatch = useDispatch();

  // const timerRef = useRef<any>(null);
  const markToDisappear = useRef<any>(null);

  const sendMessage = useCallback(
    (payload: any) => {
      if (!isPresenting) return;

      const scrollContainer = viewerControlRef.current
        ? getScrollContainer(viewerControlRef.current)
        : null;
      const viewerState = {
        ...(scrollContainer ? getScrollInfo(scrollContainer) : {}),
        ...(viewerControlRef.current ? getZoomScaleInfo(viewerControlRef.current) : {}),
        ...(viewerControlRef.current ? getDocumentRotationInfo(viewerControlRef.current) : {}),
      };

      const messagePayload = {
        ...presentationContextRef.current,
        ...viewerState,
        ...payload,
      };

      messagePayload.fileId = fileId;

      mergePresentationContext(messagePayload);

      const fullMessage = {
        fileId,
        target: 'present',
        room: present,
        ...messagePayload,
        message: messagePayload,
      };

      sendMessageApi({ message: fullMessage });
    },
    [isPresenting, fileId, mergePresentationContext, present, sendMessageApi, viewerControlRef],
  );

  const switchDocument = useCallback(
    (doc: any, goToPage: number = 1, forcePresent: boolean = false) => {
      const hasGlobalPrefix = Boolean(String(doc.globalPagePrefix || '').trim());

      // Build query string solely from the new document metadata
      const queryParts = [`folderId=${doc.folderId}`, `goToPage=${goToPage}`, `pageCount=${doc.pageCount}`];
      if (hasGlobalPrefix) {
        queryParts.push(`startPage=${doc.startPage}`, `tab=${doc.tab}`, `globalPagePrefix=${doc.globalPagePrefix}`);
      }

      // Add exhibitNumber if available
      if (doc.exhibitNumber) {
        queryParts.push(`exhibitNumber=${doc.exhibitNumber}`);
      }

      // view=share is always added in PresentView mode
      queryParts.push('view=share');

      // Include the present token if currently presenting
      if (present || forcePresent) {
        queryParts.push(`present=public`);
      }

      const queryString = `?${queryParts.join('&')}`;

      // Replace the path segment with the new doc ID
      let newPath = history.location.pathname;
      if (newPath.includes('/present-view')) {
        newPath = newPath.replace(/(\/files\/)[^/]+/, `$1${doc.id}`);
      } else {
        newPath = newPath.replace(/\/files\/[^/]+/, `/files/${doc.id}`);
      }

      history.replace(newPath + queryString);
      dispatch(urlChanged());
    },
    [dispatch, present],
  );

  // Helper to extract file details when an override is provided
  const getOverrideFileDetails = (overrideDoc?: any, overrideGoToPage?: number) => {
    if (!overrideDoc) return null;
    return {
      globalPagePrefix: overrideDoc.globalPagePrefix?.toUpperCase(),
      startPage: overrideDoc.startPage,
      pageCount: overrideDoc.pageCount,
      tab: overrideDoc.tab,
      goToPage: overrideGoToPage || overrideDoc.goToPage || overrideDoc.localPageNumber,
      name: overrideDoc?.bundleLocations[0]?.fileName || overrideDoc?.name,
      fileId: overrideDoc.id,
    };
  };

  const getFullMessageForStartPresenting = useCallback(
    (overrideDoc?: any, overrideGoToPage?: number) => {
      // ─────────────────────────────────────────────
      // if we were given an overrideDoc, use it; otherwise fall back
      // to the previous cascade
      const rawOverride = getOverrideFileDetails(overrideDoc, overrideGoToPage);
      const currentFileDetails = rawOverride
        ? rawOverride
        : showGlobalPaging ||
            isGlobalPageLookup ||
            (firstParentTriaBundleFileDetails &&
              Object.keys(firstParentTriaBundleFileDetails).length > 0) ||
            (isPresentModePage &&
              currentFileMetaData &&
              Object.keys(currentFileMetaData).includes('folderId'))
          ? {
              ...currentFileMetaData,
              ...(Object.keys(firstParentTriaBundleFileDetails).length > 0 &&
                firstParentTriaBundleFileDetails),
              name: currentFileMetaData?.bundleLocations[0]?.fileName || currentFileMetaData?.name,
            }
          : Object.keys(queryParam).includes('startPage')
            ? {
                ...queryParam,
                name:
                  currentFileMetaData?.bundleLocations[0]?.fileName || currentFileMetaData?.name,
              }
            : null;
      const scrollContainer = viewerControlRef.current
        ? getScrollContainer(viewerControlRef.current)
        : null;
      const rest = {
        ...(passViewingSessionIdToReceivers && {
          viewingSessionId: options.documentID,
          prizmApiUrl: options.imageHandlerUrl,
          viewingFilePrivate: viewingFilePrivate,
        }),
        fileId: fileId,
        fileType: 'doc',
        name: currentFileDetails?.name,
        showGlobalPaging:
          showGlobalPaging ||
          Object.keys(queryParam).includes('startPage') ||
          isGlobalPageLookup ||
          (firstParentTriaBundleFileDetails &&
            Object.keys(firstParentTriaBundleFileDetails).length > 0) ||
          (isPresentModePage &&
            currentFileMetaData &&
            Object.keys(currentFileMetaData).includes('folderId')),
        ...(currentFileDetails && {
          currentFileDetails: {
            globalPagePrefix: currentFileDetails.globalPagePrefix,
            startPage: currentFileDetails.startPage,
            pageCount: currentFileDetails.pageCount,
            tab: currentFileDetails.tab,
            name: currentFileDetails.name,
            goToPage: currentFileDetails.goToPage,
          },
        }),
        ...(scrollContainer ? getScrollInfo(scrollContainer) : {}),
        ...(viewerControlRef.current ? getZoomScaleInfo(viewerControlRef.current) : {}),
        ...(viewerControlRef.current ? getDocumentRotationInfo(viewerControlRef.current) : {}),
      };
      return { markObjects: [], ...rest };
    },
    [
      showGlobalPaging,
      isGlobalPageLookup,
      firstParentTriaBundleFileDetails,
      isPresentModePage,
      currentFileMetaData,
      queryParam,
      viewerControlRef,
      passViewingSessionIdToReceivers,
      options,
      fileId,
      viewingFilePrivate,
    ],
  );

  const addCenterHelperMark = useCallback(
    (otherChng?: any, offset = 0) => {
      let lastMarkHelper: any;

      const markCreated = ({ mark }: any) => {
        const scrollMark = mark.type === SCROLL_MARK_TYPE;
        if (scrollMark) lastMarkHelper = mark;
      };

      try {
        const myMouseToolName = 'scrollHelperTool';
        const myMouseTool = MouseTools.createMouseTool(
          myMouseToolName,
          MouseTool.Type.PlaceSignature,
        );
        myMouseTool.getTemplateMark().setSignature({
          // type: SCROLL_MARK_TYPE,
          text: '............................................................................................',
          fontName: 'Aerial',
          // path: 'M0,0',
          // color: '#FFFFFF', NOT WORKING RETARDED ACUSOFT
        });
        myMouseTool.getTemplateMark().interactionMode = Mark.InteractionMode.SelectionDisabled;

        const clientX =
          getScrollContainer(viewerControlRef.current)?.getBoundingClientRect().x +
          getScrollContainer(viewerControlRef.current)?.clientWidth / 2;
        const clientY =
          getScrollContainer(viewerControlRef.current)?.getBoundingClientRect().y +
          getScrollContainer(viewerControlRef.current)?.clientHeight / 2;

        const createMarkHelper = () => {
          viewerControlRef.current?.setCurrentMouseTool(myMouseToolName);

          getScrollContainer(viewerControlRef.current)?.dispatchEvent(
            new MouseEvent('mousedown', {
              clientX,
              clientY: clientY + offset,
              view: window,
            }),
          );
          getScrollContainer(viewerControlRef.current)?.dispatchEvent(
            new MouseEvent('mouseup', {
              clientX,
              clientY: clientY + offset,
              view: window,
            }),
          );
          setCurrentMouseTool(selectedMouseTool);
        };

        viewerControlRef.current?.on('MarkCreated', markCreated);

        createMarkHelper();
      } catch {
        lastMarkHelper = 'error';
      } finally {
        setTimeout(() => {
          viewerControlRef.current?.off('MarkCreated', markCreated);
          if (!lastMarkHelper) {
            // DO ANOTHER ONE IF IN THE MIDDLE OF THE SCREEN
            addCenterHelperMark(otherChng, 30);
          }
        }, 250); // needed timeout so set state get's set
      }
    },
    [selectedMouseTool, setCurrentMouseTool, viewerControlRef],
  );

  const sendFullMessage = (viewerOptions: any = {}) => {
    sendMessage({ ...getFullMessageForStartPresenting(), ...viewerOptions });
  };

  const updateScrollPositionForReceivers = useMemo(
    () => (otherChng?: any) => {
      if (!viewerControlRef.current || !isPresenting) return;

      if (!presentationContextRef.current.fileId) {
        const baseMessage = getFullMessageForStartPresenting();
        mergePresentationContext(baseMessage);
      }

      if (!markBasedScrolling) {
        sendMessage(otherChng || {});
      } else {
        addCenterHelperMarkSafeRef.current = makeCancelable(
          new Promise<void>((r) => {
            addCenterHelperMark(otherChng);
            r();
          }),
        );
        addCenterHelperMarkSafeRef.current.promise.then(() => {});
      }
    },
    [
      addCenterHelperMark,
      getFullMessageForStartPresenting,
      isPresenting,
      markBasedScrolling,
      mergePresentationContext,
      sendMessage,
      viewerControlRef,
    ],
  );

  const deleteMarkToDisappear = useCallback(() => {
    if (markToDisappear.current) {
      viewerControlRef.current?.deleteMarks([markToDisappear.current]);
      markToDisappear.current = null;
    }
  }, [viewerControlRef]);

  const deletePresentMarks = useCallback(() => {
    try {
      deleteMarkToDisappear();
      const presentMarks = viewerControlRef.current
        ?.getAllMarks()
        .filter((x: any) => x.fillColor === PRESENT_COLOR);
      if (presentMarks?.length > 0) {
        viewerControlRef.current?.deleteMarks(presentMarks);
      }
    } catch (e) {
      logger.ERROR(e);
    }
  }, [deleteMarkToDisappear, viewerControlRef]);

  const startStopPresentHandler = useMemo(
    () => (mode?: 'private' | 'public', forcePresent?: boolean) => {
      const containsGpParam = Object.keys(queryParam).includes('startPage');
      const containsFolderId = Object.keys(queryParam).includes('folderId');
      const gpParamStr = `startPage=${queryParam.startPage}&tab=${queryParam.tab}&pageCount=${queryParam.pageCount}&globalPagePrefix=${queryParam.globalPagePrefix}`;
      const folderIdParamStr = `folderId=${queryParam.folderId}`;
      const viewMode = ``;
      deleteMarkToBeCreated();
      if (
        isPresenting &&
        (!forcePresent || (present === 'public' && currentFileMetaData.private))
      ) {
        leaveGroupSafeRef.current = makeCancelable(
          new Promise((r: any) => leaveAGroup('present').then(() => r())),
        );
        leaveGroupSafeRef.current.promise.then(() => {
          history.push(
            history.location.pathname.replace(/\?present.*/, ``) +
              `${containsFolderId ? `?${folderIdParamStr}` : ''}${
                containsFolderId && containsGpParam ? '&' : ''
              }${containsGpParam ? `${gpParamStr}` : ''}` +
              `${viewMode ? '&' : ''}${viewMode}`,
          );
          // timerRef.current?.stop();
          // TO-DO this might require useEffect to execute after present was set
          deletePresentMarks();
          setCurrentMouseTool(selectedMouseTool, false);
          resetPresentationContext();
          // --------------------------------------------
        });
      } else {
        const messagePayload = getFullMessageForStartPresenting();
        mergePresentationContext(messagePayload);
        startPresentingSafeRef.current = makeCancelable(
          new Promise((r: any) => {
            startPresenting({
              hearingRoomMode: mode,
              message: messagePayload,
            }).then(() => {
              if (markBasedScrolling) addCenterHelperMark();
              // timerRef.current?.stop();
              // timerRef.current = interval(() => updateScrollPositionForReceivers(), 5000);
              r();
            });
          }),
        );

        startPresentingSafeRef.current.promise.then(() => {
          setCurrentMouseTool(selectedMouseTool, true);
          history.push(
            history.location.pathname.replace(/\?present.*/, '') +
              `?${containsFolderId ? `${folderIdParamStr}&` : ''}` +
              `${containsGpParam ? `${gpParamStr}&` : ''}` +
              `present=${mode}`,
          );
        });
      }
    },
    [
      addCenterHelperMark,
      currentFileMetaData.private,
      deleteMarkToBeCreated,
      deletePresentMarks,
      getFullMessageForStartPresenting,
      isPresenting,
      leaveAGroup,
      mergePresentationContext,
      markBasedScrolling,
      present,
      queryParam,
      resetPresentationContext,
      selectedMouseTool,
      setCurrentMouseTool,
      startPresenting,
      // updateScrollPositionForReceivers,
    ],
  );

  const startStopPresentHandlerWPresentView = useMemo(
    () =>
      (
        mode?: 'private' | 'public',
        forcePresent?: boolean,
        overrideDoc?: any,
        overrideGoToPage?: number,
      ) => {
        deleteMarkToBeCreated();

        const parsedDoc = safeGetJSON(
          sessionStorage,
          `${currentCase.id}.currentlyPresenting`,
          null,
        );

        if (
          isPresenting &&
          (!forcePresent || (present === 'public' && currentFileMetaData.private))
        ) {
          leaveGroupSafeRef.current = makeCancelable(
            new Promise((r: any) => leaveAGroup('present').then(() => r())),
          );
          leaveGroupSafeRef.current.promise.then(() => {
            deletePresentMarks();
            setCurrentMouseTool(selectedMouseTool, false);
            resetPresentationContext();
            /*───────────────────────────────────────────
             * Remove present=public (or any present=val)
             * from the query‑string when we stop presenting.
             * Path and the rest of the params stay untouched.
             *───────────────────────────────────────────*/
            const urlObj = new URL(window.location.href);
            urlObj.searchParams.delete('present');
            history.replace(
              urlObj.pathname +
                (urlObj.searchParams.toString() ? `?${urlObj.searchParams.toString()}` : ''),
            );
          });
        } else {
          const messagePayload = getFullMessageForStartPresenting(parsedDoc, overrideGoToPage);
          mergePresentationContext(messagePayload);
          startPresentingSafeRef.current = makeCancelable(
            new Promise((r: any) => {
              startPresenting({
                hearingRoomMode: mode,
                message: messagePayload,
              })
                .then(() => {
                  if (markBasedScrolling) addCenterHelperMark();
                  r();
                })
                .catch((error: any) => {
                  if (error && error.isCanceled) {
                    return;
                  }
                  logger.ERROR(error);
                });
            }),
          );

          startPresentingSafeRef.current.promise
            .then(() => {
              setCurrentMouseTool(selectedMouseTool, true);
              const url = new URL(window.location.href);
              url.searchParams.set('present', mode || 'public');
              history.replace(
                url.pathname +
                  (url.searchParams.toString() ? `?${url.searchParams.toString()}` : ''),
              );
            })
            .catch((error: any) => {
              if (error && error.isCanceled) {
                return;
              }
              logger.ERROR(error);
            });
        }
      },
    [
      addCenterHelperMark,
      currentCase.id,
      currentFileMetaData.private,
      deleteMarkToBeCreated,
      deletePresentMarks,
      getFullMessageForStartPresenting,
      isPresenting,
      leaveAGroup,
      mergePresentationContext,
      markBasedScrolling,
      present,
      resetPresentationContext,
      selectedMouseTool,
      setCurrentMouseTool,
      startPresenting,
    ],
  );

  const MarkCreated = useCallback(
    ({ mark }: any) => {
      if (isPresenting) {
        mark.interactionMode = Mark.InteractionMode.SelectionDisabled;
        mark.setData('visible', 'true');
        try {
          viewerControlRef.current?.serializeMarks([mark]).then((markObjects: any) => {
            const scrollMark = mark.type === SCROLL_MARK_TYPE;
            const pageNumberMark = mark.type === PAGENUMBER_MARK_TYPE;

            if (pageNumberMark) return;

            if (!presentationContextRef.current.fileId) {
              const baseMessage = getFullMessageForStartPresenting();
              mergePresentationContext(baseMessage);
            }

            sendMessage({ markObjects });

            if (scrollMark) {
              viewerControlRef.current?.deleteMarks([mark]);
              return;
            }

            if (HIGHLIGHTSDISAPPEAR && markToDisappear.current) {
              const m = markToDisappear.current;
              makeMarkDisappear(
                m,
                () => viewerControlRef.current?.deleteMarks([m]),
                () => !isPresenting,
              );
            }

            if (markToDisappear.current !== mark) markToDisappear.current = mark;
          });
        } catch (e) {
          logger.ERROR(e);
        }
      }
    },
    [
      getFullMessageForStartPresenting,
      isPresenting,
      mergePresentationContext,
      sendMessage,
      viewerControlRef,
    ],
  );

  const MarkClickedHandler = useCallback(
    ({ mark }: any) => {
      if (mark && isPresenting) {
        mark.interactionMode = Mark.InteractionMode.SelectionDisabled;
        mark.visible = false;
        mark.setData('visible', 'false');
        if (HIGHLIGHTSDISAPPEAR) {
          viewerControlRef.current?.serializeMarks([mark]).then((markObjects: any) => {
            if (!presentationContextRef.current.fileId) {
              const baseMessage = getFullMessageForStartPresenting();
              mergePresentationContext(baseMessage);
            }
            sendMessage({ markObjects });
            viewerControlRef.current?.deleteMarks([mark]);
            markToDisappear.current = null;
          });
        }
      }
    },
    [
      getFullMessageForStartPresenting,
      isPresenting,
      mergePresentationContext,
      sendMessage,
      viewerControlRef,
    ],
  );

  useEffect(() => {
    viewerControlRef.current?.on('MarkCreated', MarkCreated);
    viewerControlRef.current?.on('Click', MarkClickedHandler);

    const vcr = viewerControlRef.current;
    return () => {
      vcr?.off('MarkCreated', MarkCreated);
      vcr?.off('Click', MarkClickedHandler);
    };
  }, [MarkClickedHandler, MarkCreated, viewerControlRef]);

  useEffect(() => {
    // const tr = timerRef.current;

    return () => {
      startPresentingSafeRef.current?.cancel();
      leaveGroupSafeRef.current?.cancel();
      addCenterHelperMarkSafeRef.current?.cancel();
      resetPresentationContext();
      // tr?.stop();
    };
  }, [resetPresentationContext]);

  return {
    startStopPresentHandler,
    startStopPresentHandlerWPresentView,
    updateScrollPositionForReceivers,
    sendFullMessage,
    switchDocument,
  };
};
